import supabaseClient from '@/api/supabaseClient.ts';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';
import type {
  GovernanceCheckPreviewRequest,
  GovernanceCheckPreviewResponse,
} from '$/requests/GovernanceCheck.ts';
import { BackendErrorResponse } from '@/types/response/BackendErrorResponse';

/** Coalesce parallel preview calls (e.g. React Strict Mode double-mount). */
const previewInFlight = new Map<string, Promise<GovernanceCheckPreviewResponse>>();

export async function fetchGovernanceCheckPreview(
  body: GovernanceCheckPreviewRequest,
): Promise<GovernanceCheckPreviewResponse> {
  const key = body.decision_id;
  const existing = previewInFlight.get(key);
  if (existing) return existing;

  const promise = (async () => {
    const { data, error } = await supabaseClient.functions.invoke('governance-check-preview', {
      body,
    });

    if (error) {
      if (error instanceof FunctionsHttpError) {
        const errorResponse: BackendErrorResponse = await error.context.json();
        throw new Error(errorResponse.errors?.join(', ') ?? 'Recommendation preview failed');
      }
      throw new Error(error.message || 'Recommendation preview failed');
    }

    const payload = data as { data?: GovernanceCheckPreviewResponse; errors?: string[] };
    if (payload?.errors?.length) {
      throw new Error(payload.errors.join(', '));
    }

    if (!payload?.data) {
      throw new Error('Recommendation preview failed');
    }

    return payload.data;
  })();

  previewInFlight.set(key, promise);
  try {
    return await promise;
  } finally {
    if (previewInFlight.get(key) === promise) {
      previewInFlight.delete(key);
    }
  }
}

type UseGovernanceCheckPreviewOptions = {
  enabled?: boolean;
};

const useGovernanceCheckPreview = (
  decisionId: string | undefined,
  options: UseGovernanceCheckPreviewOptions = {},
) => {
  const enabled = Boolean(decisionId) && options.enabled !== false;

  return useQuery({
    queryKey: ['governance-check-preview', decisionId],
    queryFn: () => fetchGovernanceCheckPreview({ decision_id: decisionId! }),
    enabled,
    staleTime: Infinity,
    retry: 1,
  });
};

export default useGovernanceCheckPreview;
